import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useApp } from "../app-context";
import {
  companyStageHistory, createCompany, createPerson, personEmail, personName, saleName, updateCompany, updatePerson,
  type Company, type CompanyInput, type Person, type PersonInput,
} from "../data";
import { CompanyLogo, PersonTile } from "../CompanyLogo";
import { href } from "../router";
import { STAGES, parseUsd, stageLabel, type Stage } from "../stages";
import { ErrorNote, Field, plural, useAction, useLoad } from "../ui";
import { ago } from "./Dashboard";

/** Slide-over on desktop, full screen on a phone. Esc or the backdrop closes it. */
function Panel({ title, icon, crumb, onClose, children }: {
  title: string; icon?: ReactNode; crumb?: ReactNode; onClose: () => void; children: ReactNode;
}) {
  const ref = useRef<HTMLElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  // Once per opening: focus the first field, and close on Esc. (Depending on
  // onClose here would re-run every render and steal focus while typing.)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close.current(); };
    window.addEventListener("keydown", onKey);
    ref.current?.querySelector<HTMLElement>("input, select, textarea")?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return (
    <div className="panel-backdrop" onClick={onClose}>
      <aside ref={ref} className="panel" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        {crumb ? <nav className="crumb" aria-label="Back">{crumb}</nav> : null}
        <header className="panel-head">
          <span className="panel-title">{icon}<h2>{title}</h2></span>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">×</button>
        </header>
        {children}
      </aside>
    </div>
  );
}

export function NotFoundPanel({ what, onClose }: { what: string; onClose: () => void }) {
  return (
    <Panel title={`No such ${what}`} onClose={onClose}>
      <p className="muted">It may have been deleted.</p>
    </Panel>
  );
}

function Timestamps({ created, updated }: { created: string | null; updated: string }) {
  const fmt = (iso: string) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  return <p className="muted small">Created {created ? fmt(created) : "— (before this was recorded)"} · Updated {fmt(updated)}</p>;
}

function History({ id, version }: { id: number; version: string }) {
  const { salesById } = useApp();
  // version (the record's updated_at) re-reads the log after every save.
  const load = useLoad(() => companyStageHistory(id), [id, version]);
  return (
    <section className="history" aria-label="Stage history">
      <h3>Stage history</h3>
      <ErrorNote error={load.error} />
      {load.data && load.data.length === 0 ? <p className="muted small">No stage changes recorded yet.</p> : null}
      <ol>
        {(load.data ?? []).map((c) => (
          <li key={c.id}>
            <span>{c.from_stage ? <>{stageLabel(c.from_stage)} → <strong>{stageLabel(c.to_stage)}</strong></> : <>Added at <strong>{stageLabel(c.to_stage)}</strong></>}</span>
            <span className="muted small">
              {c.changed_by ? `${saleName(salesById.get(c.changed_by))} · ` : ""}
              <time dateTime={c.changed_at} title={new Date(c.changed_at).toLocaleString()}>{ago(c.changed_at)}</time>
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

const moneyText = (v: number | null) => (v == null ? "" : String(v));
const blank = (s: string) => s.trim() || null;

// ---------------------------------------------------------------- company

export function CompanyPanel({ company, people, onClose, onSaved }: {
  company: Company | null; people: Person[]; onClose: () => void; onSaved: (c: Company) => void;
}) {
  const { me, sales, config } = useApp();
  const action = useAction();
  const [f, setF] = useState(() => ({
    name: company?.name ?? "", website: company?.website ?? "", sector: company?.sector ?? "", notes: company?.description ?? "",
  }));
  const [stage, setStage] = useState<Stage>(company?.stage ?? "idea");
  const [value, setValue] = useState(moneyText(company?.deal_value ?? null));
  const [owner, setOwner] = useState(company ? (company.sales_id ? String(company.sales_id) : "") : String(me?.id ?? ""));
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const linked = company ? people.filter((p) => p.company_id === company.id).sort((a, b) => personName(a).localeCompare(personName(b))) : [];

  const save = async (e: FormEvent) => {
    e.preventDefault();
    let saved: Company | undefined;
    const ok = await action.run(async () => {
      if (!f.name.trim()) throw new Error("A company needs a name.");
      const input: CompanyInput = {
        name: f.name.trim(), website: blank(f.website), sector: blank(f.sector), description: blank(f.notes),
        stage, deal_value: parseUsd(value), sales_id: owner ? Number(owner) : null,
      };
      saved = company ? await updateCompany(company.id, input) : await createCompany(input);
    });
    if (ok && saved) onSaved(saved);
  };

  return (
    <Panel title={company ? company.name : "Add company"} onClose={onClose}
      icon={company ? <CompanyLogo name={company.name} website={company.website} size="lg" /> : undefined}>
      <form className="form" onSubmit={save}>
        <Field label="Name"><input value={f.name} onChange={set("name")} /></Field>
        <div className="grid2">
          <div className="field-stack">
            <Field label="Website"><input value={f.website} onChange={set("website")} placeholder="example.com" /></Field>
            {f.website.trim() ? null : <p className="hint muted small">Add their website and the logo comes in.</p>}
          </div>
          <Field label="Industry">
            <select value={f.sector} onChange={set("sector")}>
              <option value="">None</option>
              {config.companySectors.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </Field>
        </div>
        <div className="grid3">
          <Field label="Stage">
            <select value={stage} onChange={(e) => setStage(e.target.value as Stage)}>
              {STAGES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </Field>
          <Field label="Deal value (USD)">
            <input inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} placeholder="$0" />
          </Field>
          <Field label="Owner">
            <select value={owner} onChange={(e) => setOwner(e.target.value)}>
              <option value="">Unassigned</option>
              {sales.filter((s) => !s.disabled || String(s.id) === owner).map((s) => <option key={s.id} value={s.id}>{saleName(s)}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Notes"><textarea rows={4} value={f.notes} onChange={set("notes")} /></Field>
        <ErrorNote error={action.error} />
        <div className="form-actions">
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn primary" disabled={action.busy}>{action.busy ? "Saving…" : company ? "Save" : "Add company"}</button>
        </div>
      </form>
      {company ? (
        <>
          <section className="linked" aria-label="People at this company">
            <div className="linked-head">
              <h3>People <span className="count">{linked.length}</span></h3>
              <a className="btn small-btn" href={href({ page: "companies", id: company.id, addPerson: true })}>Add person</a>
            </div>
            {linked.length === 0 ? <p className="muted small">No one here yet. Add the people you're talking to.</p> : (
              <ul className="people">
                {linked.map((p) => (
                  <li key={p.id}><PersonRow person={p} /></li>
                ))}
              </ul>
            )}
          </section>
          <Timestamps created={company.created_at} updated={company.updated_at} />
          <History id={company.id} version={company.updated_at} />
        </>
      ) : null}
    </Panel>
  );
}

// ---------------------------------------------------------------- person

/** One person in a list: links to their editor. */
export function PersonRow({ person: p }: { person: Person }) {
  return (
    <a className="person-row" href={href({ page: "person", id: p.id })}>
      <PersonTile name={personName(p)} />
      <span className="person-text">
        <strong>{personName(p)}</strong>
        <span className="muted small">{[p.title, personEmail(p)].filter(Boolean).join(" · ") || "No title or email yet"}</span>
      </span>
    </a>
  );
}

/**
 * Add or edit one person. Opened from their company's panel (with a way back
 * to it), or on its own for someone not linked to a company yet, where the
 * Company select is how they get linked.
 */
export function PersonPanel({ person, company, companies, onClose, onSaved }: {
  person: Person | null; company: Company | undefined; companies: Company[]; onClose: () => void; onSaved: (p: Person) => void;
}) {
  const action = useAction();
  const [f, setF] = useState(() => ({
    first_name: person?.first_name ?? "", last_name: person?.last_name ?? "", title: person?.title ?? "",
    email: person?.email_jsonb?.[0]?.email ?? "", phone: person?.phone_jsonb?.[0]?.number ?? "",
    linkedin_url: person?.linkedin_url ?? "", company_id: company ? String(company.id) : "",
    notes: person?.background ?? "",
  }));
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const extraEmails = Math.max(0, (person?.email_jsonb?.length ?? 0) - 1);
  const extraPhones = Math.max(0, (person?.phone_jsonb?.length ?? 0) - 1);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    let saved: Person | undefined;
    const ok = await action.run(async () => {
      if (!f.first_name.trim() && !f.last_name.trim()) throw new Error("A person needs a name.");
      // The first email/phone is edited here; any further ones a record already
      // has (the previous UI allowed several) are kept untouched.
      const emails = [...(person?.email_jsonb ?? [])];
      const phones = [...(person?.phone_jsonb ?? [])];
      if (f.email.trim()) emails[0] = { email: f.email.trim(), type: emails[0]?.type ?? "Work" }; else emails.splice(0, 1);
      if (f.phone.trim()) phones[0] = { number: f.phone.trim(), type: phones[0]?.type ?? "Work" }; else phones.splice(0, 1);
      const input: PersonInput = {
        first_name: blank(f.first_name), last_name: blank(f.last_name), title: blank(f.title),
        email_jsonb: emails, phone_jsonb: phones, linkedin_url: blank(f.linkedin_url),
        company_id: f.company_id ? Number(f.company_id) : null, background: blank(f.notes),
      };
      saved = person ? await updatePerson(person.id, input) : await createPerson(input);
    });
    if (ok && saved) onSaved(saved);
  };

  const crumb = company ? (
    <a href={href({ page: "companies", id: company.id })}>
      <span aria-hidden="true">←</span> <CompanyLogo name={company.name} website={company.website} size="sm" /> {company.name}
    </a>
  ) : null;

  return (
    <Panel title={person ? personName(person) : "Add person"} crumb={crumb} onClose={onClose}
      icon={person ? <PersonTile name={personName(person)} size="lg" /> : undefined}>
      {!company && person ? <p className="note small">Not linked to a company yet. Choose one below to put them in the pipeline.</p> : null}
      <form className="form" onSubmit={save}>
        <div className="grid2">
          <Field label="First name"><input value={f.first_name} onChange={set("first_name")} /></Field>
          <Field label="Last name"><input value={f.last_name} onChange={set("last_name")} /></Field>
        </div>
        <Field label="Title"><input value={f.title} onChange={set("title")} /></Field>
        <Field label="Company">
          <select value={f.company_id} onChange={set("company_id")}>
            <option value="">Not linked</option>
            {companies.slice().sort((a, b) => a.name.localeCompare(b.name)).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <div className="grid2">
          <div className="field-stack">
            <Field label="Email"><input type="email" value={f.email} onChange={set("email")} /></Field>
            {extraEmails ? <p className="hint muted small">+{plural(extraEmails, "more address", "more addresses")} kept</p> : null}
          </div>
          <div className="field-stack">
            <Field label="Phone"><input type="tel" value={f.phone} onChange={set("phone")} /></Field>
            {extraPhones ? <p className="hint muted small">+{plural(extraPhones, "more number")} kept</p> : null}
          </div>
        </div>
        <Field label="LinkedIn"><input type="url" value={f.linkedin_url} onChange={set("linkedin_url")} placeholder="https://linkedin.com/in/…" /></Field>
        <Field label="Notes"><textarea rows={5} value={f.notes} onChange={set("notes")} /></Field>
        <ErrorNote error={action.error} />
        <div className="form-actions">
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn primary" disabled={action.busy}>{action.busy ? "Saving…" : person ? "Save" : "Add person"}</button>
        </div>
      </form>
      {person ? <Timestamps created={person.created_at} updated={person.updated_at} /> : null}
    </Panel>
  );
}
